/**
 * Does this record actually exist yet?
 *
 * A backend-minted id is always **positive**. A surface holding a record that
 * has never been saved still has to give it *some* identity — a React key, a
 * lookup, a commit target — and the convention in this codebase is to
 * synthesise one **below zero** so the two can never collide
 * (`components/ai-review/compact/pending-id.ts` mints them for the AI-extract
 * review's pending rows).
 *
 * **The point of naming this test is that `if (record)` is not it.** A pending
 * record is a perfectly real object with a perfectly real id field, so anything
 * that only checks for presence treats it as saved — and then fetches by an id
 * no row answers to, offers a control with nothing behind it, or shows a
 * sub-record collection belonging to a contact that does not exist. Three
 * separate defects on the review surface were that one mistake (VALE-977).
 *
 * Use it wherever a component does something *because the record has an id*:
 * fetching by it, writing to it, or rendering a collection keyed by it.
 * Rendering the record's own values needs no such test — those are present
 * either way, which is exactly why this is easy to forget.
 */
export function isPersistedRecord(record: { id: number } | null | undefined) {
  return !!record && record.id > 0;
}

/** {@link isPersistedRecord}, for a bare id. */
export function isPersistedId(id: number | null | undefined) {
  return typeof id === 'number' && id > 0;
}
